import { useState } from "react";
import { useMutation } from "@tanstack/react-query";
import { CoursemakerUploadRequest, CourseJobStartResponse } from "@vixi/types";
import { AxiosProgressEvent } from "axios";
import { isMock } from "@/mocks";
import api, { formdataFromObj } from "@vixi/api";
import { sleepAsync, logger } from "@vixi/utils";

const mockStartJob = async (_body: CoursemakerUploadRequest): Promise<CourseJobStartResponse> => {
  await sleepAsync(1000);
  return {
    jobId: `mock-job-${Date.now()}`,
    status: 0,
    message: "Job started successfully",
  };
};

export const useStartCourseJob = () => {
  const [uploadProgress, setUploadProgress] = useState(0);

  const mutationFn = async (body: CoursemakerUploadRequest): Promise<CourseJobStartResponse> => {
    logger.info("🚀 STEP 1: Starting async course generation job", body);

    if (isMock("generateCourse_ebook")) {
      logger.warn("⚠️ Using MOCK data - generateCourse_ebook is enabled in mocks");
      logger.warn("⚠️ This will return a fake jobId that won't work with the backend!");
      return mockStartJob(body);
    }

    // ============================================
    // STEP 1: POST /api/CourseMaker/async
    // ============================================
    // This is the FIRST request that starts the job
    // Response: { jobId: string, status: number, message: string, error?: string, errorAr?: string }
    // IMPORTANT: This returns ONLY jobId, NOT course data
    // Course data will be fetched later when job is completed via GET /api/CourseMaker/jobs/{jobId}/result
    
    const endpoint = api.endpoints.uploadEbookAsync; // "CourseMaker/async"
    const baseURL = api.getDefaultConfig().baseURL || "/api";
    const fullURL = `${baseURL}/${endpoint}`; // "/api/CourseMaker/async"
    
    logger.info(`📤 STEP 1: Calling POST ${fullURL}`);
    logger.info(`📤 This is the FIRST request - it will return jobId only`);
    logger.info(`📤 Request body:`, { lang: body.lang, pdfFilesCount: body.pdfFiles?.length });
    
    const response = await api.post<CourseJobStartResponse>(
      endpoint,
      formdataFromObj(body),
      {
        onUploadProgress: (progress: AxiosProgressEvent) => {
          setUploadProgress(
            progress.total ? (progress.loaded / progress.total) * 100 : progress.bytes,
          );
        },
      },
    );

    logger.info("✅ STEP 1 COMPLETE: Received response from POST /api/CourseMaker/async");
    logger.info("✅ Response status:", response.status);
    logger.info("✅ Response data:", response.data);
    logger.info("✅ JobId from API:", response.data.jobId);
    
    if (!response.data.jobId) {
      logger.error("❌ ERROR: No jobId in response! Response:", response.data);
      throw new Error("No jobId received from API");
    }
    
    logger.info(`✅ STEP 1 SUCCESS: Got jobId "${response.data.jobId}" - This will be used for polling`);
    
    return response.data;
  };

  const mutation = useMutation({
    mutationKey: ["start-course-job"],
    mutationFn,
  });

  return { ...mutation, uploadProgress };
};
